// src/DocumentationPalette.ts
var SOFTWAREIQ_DOCUMENTATION_PALETTES = {
  "light": {
    "--background": "#f5f6fb",
    "--background-soft": "#e5ebfa",
    "--surface": "rgba(255, 255, 255, 0.74)",
    "--surface-strong": "rgba(255, 255, 255, 0.92)",
    "--surface-soft": "rgba(247, 249, 255, 0.84)",
    "--surface-dark": "#18336c",
    "--heading": "#102235",
    "--text": "#102235",
    "--muted": "#59657c",
    "--line": "rgba(44, 70, 122, 0.12)",
    "--line-strong": "rgba(44, 70, 122, 0.18)",
    "--interactive": "#2952a3",
    "--interactive-strong": "#18336c",
    "--action-primary": "#2952a3",
    "--action-primary-hover": "#18336c",
    "--on-action": "#fff",
    "--primary": "var(--interactive)",
    "--primary-strong": "var(--interactive-strong)",
    "--accent": "#0f8a6a",
    "--accent-soft": "rgba(15, 138, 106, 0.12)",
    "--shadow": "0 24px 70px rgba(35, 55, 108, 0.12)",
    "--shadow-soft": "0 14px 34px rgba(35, 55, 108, 0.08)",
    "--radius-xl": "32px",
    "--radius-lg": "24px",
    "--radius-md": "18px"
  },
  "dark": {
    "--background": "#09111f",
    "--background-soft": "#12213f",
    "--surface": "rgba(16, 24, 42, 0.78)",
    "--surface-strong": "rgba(19, 31, 56, 0.9)",
    "--surface-soft": "rgba(18, 33, 63, 0.74)",
    "--surface-dark": "#dce8ff",
    "--heading": "#ecf3ff",
    "--text": "#ecf3ff",
    "--muted": "#a6b4ce",
    "--line": "rgba(124, 158, 224, 0.16)",
    "--line-strong": "rgba(124, 158, 224, 0.24)",
    "--interactive": "#9ebcff",
    "--interactive-strong": "#dce8ff",
    "--action-primary": "#3567c6",
    "--action-primary-hover": "#2952a3",
    "--on-action": "#fff",
    "--primary": "var(--interactive)",
    "--primary-strong": "var(--interactive-strong)",
    "--accent": "#39c9a0",
    "--accent-soft": "rgba(57, 201, 160, 0.16)",
    "--shadow": "0 24px 70px rgba(0, 0, 0, 0.35)",
    "--shadow-soft": "0 14px 34px rgba(0, 0, 0, 0.24)",
    "--radius-xl": "32px",
    "--radius-lg": "24px",
    "--radius-md": "18px"
  }
};

// src/McpGuide.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function Code({ children, label }) {
  return /* @__PURE__ */ jsxs("figure", { className: "siq-mcp-code", children: [
    /* @__PURE__ */ jsx("figcaption", { children: label }),
    /* @__PURE__ */ jsx("pre", { children: /* @__PURE__ */ jsx("code", { children }) })
  ] });
}
function SoftwareIqMcpGuide({ content, connection, resources, showVerification = true, renderCode }) {
  const stepCode = (step) => {
    if (!step.code) return null;
    const label = step.codeLabel || (step.language === "sh" ? "Terminal" : step.language === "toml" ? "~/.codex/config.toml" : "Server URL");
    return renderCode ? renderCode(step.code, step.language || "text", label) : /* @__PURE__ */ jsx(Code, { label, children: step.code });
  };
  const clientOrder = ["claude", "claude-code", "codex"];
  return /* @__PURE__ */ jsxs("div", { className: "siq-mcp-guide", children: [
    /* @__PURE__ */ jsxs("aside", { className: "siq-mcp-guide__nav", children: [
      /* @__PURE__ */ jsx("p", { children: "In this guide" }),
      /* @__PURE__ */ jsxs("nav", { "aria-label": "MCP page sections", children: [
        /* @__PURE__ */ jsx("a", { href: "#before-you-connect", children: "Before you connect" }),
        /* @__PURE__ */ jsx("a", { href: "#setup", children: "Connect your assistant" }),
        /* @__PURE__ */ jsx("a", { href: "#reconnect", children: "Reconnect" }),
        /* @__PURE__ */ jsx("a", { href: "#prompts", children: "Prompting tips" }),
        /* @__PURE__ */ jsx("a", { href: "#troubleshooting", children: "Connection help" }),
        /* @__PURE__ */ jsx("a", { href: "#resources", children: "Further reading" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "siq-mcp-guide__content", children: [
      /* @__PURE__ */ jsxs("section", { id: "before-you-connect", className: "siq-mcp-section", children: [
        /* @__PURE__ */ jsxs("header", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Before you connect" }),
          /* @__PURE__ */ jsx("p", { children: "Check your MCP access in Research, then use the setup for your chosen assistant." })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "siq-mcp-prerequisites", children: content.prerequisites.map((item, index) => /* @__PURE__ */ jsxs("article", { children: [
          /* @__PURE__ */ jsx("span", { className: "siq-mcp-number", children: index + 1 }),
          /* @__PURE__ */ jsx("h3", { children: item.title }),
          /* @__PURE__ */ jsx("p", { children: item.description })
        ] }, item.title)) })
      ] }),
      /* @__PURE__ */ jsxs("section", { id: "setup", className: "siq-mcp-section", children: [
        /* @__PURE__ */ jsxs("header", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Connect your assistant" }),
          /* @__PURE__ */ jsx("p", { children: "Choose your assistant, add SoftwareIQ, and sign in to your account." })
        ] }),
        connection,
        /* @__PURE__ */ jsx("div", { className: "siq-mcp-clients", children: [...content.clients].sort((a, b) => clientOrder.indexOf(a.id) - clientOrder.indexOf(b.id)).map((client) => /* @__PURE__ */ jsxs("details", { id: `setup-${client.id}`, className: "siq-mcp-client", open: client.id === "claude", children: [
          /* @__PURE__ */ jsxs("summary", { children: [
            /* @__PURE__ */ jsxs("span", { children: [
              /* @__PURE__ */ jsx("strong", { children: client.title }),
              /* @__PURE__ */ jsx("small", { children: client.subtitle })
            ] }),
            /* @__PURE__ */ jsx("span", { className: "siq-mcp-client__toggle", "aria-hidden": "true", children: "+" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "siq-mcp-client__body", children: [
            /* @__PURE__ */ jsx("p", { children: client.intro }),
            /* @__PURE__ */ jsx("ol", { className: "siq-mcp-steps", children: client.steps.map((step, index) => /* @__PURE__ */ jsxs("li", { children: [
              /* @__PURE__ */ jsx("span", { className: "siq-mcp-number", "aria-hidden": "true", children: index + 1 }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("h3", { children: step.title }),
                /* @__PURE__ */ jsx("p", { children: step.description }),
                step.link && /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsxs("a", { href: step.link.href, target: "_blank", rel: "noopener noreferrer", children: [
                  step.link.label,
                  " \u2197"
                ] }) }),
                stepCode(step),
                step.note && /* @__PURE__ */ jsx("p", { className: "siq-mcp-note", children: step.note })
              ] })
            ] }, step.title)) }),
            /* @__PURE__ */ jsxs("a", { href: client.reference, target: "_blank", rel: "noopener noreferrer", children: [
              "Official ",
              client.title,
              " setup instructions \u2197"
            ] })
          ] })
        ] }, client.id)) }),
        showVerification && /* @__PURE__ */ jsxs("div", { className: "siq-mcp-verify", children: [
          /* @__PURE__ */ jsx("h3", { children: "Confirm the connection" }),
          /* @__PURE__ */ jsx("p", { children: "Ask your agent:" }),
          /* @__PURE__ */ jsx("blockquote", { children: content.verificationPrompt }),
          /* @__PURE__ */ jsx("p", { children: "The tools available to your assistant should match your approved datasets. A connection alone does not confirm data access." })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("section", { id: "reconnect", className: "siq-mcp-section", children: [
        /* @__PURE__ */ jsxs("header", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Reconnect" }),
          /* @__PURE__ */ jsx("p", { children: "If your assistant asks you to sign in again or your connection stops working, follow these steps. You usually do not need to add SoftwareIQ again." })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "siq-mcp-reconnect", children: content.reconnect.map((assistant) => /* @__PURE__ */ jsxs("article", { children: [
          /* @__PURE__ */ jsx("h3", { children: assistant.title }),
          /* @__PURE__ */ jsx("ol", { children: assistant.steps.map((step) => /* @__PURE__ */ jsxs("li", { children: [
            /* @__PURE__ */ jsx("strong", { children: step.title }),
            /* @__PURE__ */ jsx("p", { children: step.description }),
            step.link && /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsxs("a", { href: step.link.href, target: "_blank", rel: "noopener noreferrer", children: [
              step.link.label,
              " \u2197"
            ] }) }),
            stepCode(step)
          ] }, step.title)) })
        ] }, assistant.title)) })
      ] }),
      /* @__PURE__ */ jsxs("section", { id: "prompts", className: "siq-mcp-section", children: [
        /* @__PURE__ */ jsxs("header", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Prompting tips" }),
          /* @__PURE__ */ jsx("p", { children: "Ask a research question in plain language. You do not need to supply company IDs or tool names." })
        ] }),
        /* @__PURE__ */ jsx("dl", { className: "siq-mcp-tips", children: content.promptTips.map((item) => /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("dt", { children: item.title }),
          /* @__PURE__ */ jsx("dd", { children: item.description })
        ] }, item.title)) }),
        /* @__PURE__ */ jsx("h3", { children: "Example research questions" }),
        /* @__PURE__ */ jsx("div", { className: "siq-mcp-prompts", children: content.prompts.map((prompt) => /* @__PURE__ */ jsxs("article", { children: [
          /* @__PURE__ */ jsx("h4", { children: prompt.title }),
          /* @__PURE__ */ jsx("p", { children: prompt.text })
        ] }, prompt.title)) })
      ] }),
      /* @__PURE__ */ jsxs("section", { id: "troubleshooting", className: "siq-mcp-section", children: [
        /* @__PURE__ */ jsxs("header", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Connection help" }),
          /* @__PURE__ */ jsx("p", { children: "Use the symptom below to find the next step." })
        ] }),
        /* @__PURE__ */ jsx("dl", { className: "siq-mcp-help", children: content.troubleshooting.map((item) => /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("dt", { children: item.title }),
          /* @__PURE__ */ jsx("dd", { children: item.description })
        ] }, item.title)) }),
        /* @__PURE__ */ jsxs("p", { children: [
          "For setup help, email ",
          /* @__PURE__ */ jsx("a", { href: "mailto:support@softwareiq.io", children: "support@softwareiq.io" }),
          ". For MCP access inquiries, email ",
          /* @__PURE__ */ jsx("a", { href: "mailto:sales@softwareiq.io", children: "sales@softwareiq.io" }),
          "."
        ] })
      ] }),
      /* @__PURE__ */ jsxs("section", { id: "resources", className: "siq-mcp-section", children: [
        /* @__PURE__ */ jsxs("header", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Further reading" }),
          /* @__PURE__ */ jsx("p", { children: "Dataset definitions, delivery options, and technical guidance for agents." })
        ] }),
        resources,
        /* @__PURE__ */ jsx("a", { href: content.AGENT_INSTRUCTIONS_URL, className: "siq-mcp-agent-link", children: "Agent reference (Markdown) \u2197" }),
        /* @__PURE__ */ jsx("p", { className: "siq-mcp-resource-note", children: "The agent reference covers identifiers, pagination, missing values, and data interpretation. It complements this setup guide." })
      ] })
    ] })
  ] });
}

// src/index.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var SOFTWAREIQ_THEME_COOKIE = "softwareiq-theme";
var SOFTWAREIQ_THEME_STORAGE_KEY = "softwareiq-theme";
var SOFTWAREIQ_ANALYTICS_CONSENT_COOKIE = "softwareiq-analytics-consent";
var SOFTWAREIQ_ANALYTICS_CONSENT_EVENT = "softwareiq:analytics-consent-change";
var SOFTWAREIQ_ANALYTICS_PREFERENCES_EVENT = "softwareiq:analytics-preferences-open";
var isSoftwareIqTheme = (value) => value === "light" || value === "dark";
var readCookieValue = (cookie, name) => cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith(`${name}=`))?.slice(name.length + 1);
var isSoftwareIqAnalyticsConsent = (value) => value === "accepted" || value === "rejected";
function readSoftwareIqAnalyticsConsent() {
  if (typeof document === "undefined") return null;
  const value = readCookieValue(document.cookie, SOFTWAREIQ_ANALYTICS_CONSENT_COOKIE);
  return isSoftwareIqAnalyticsConsent(value) ? value : null;
}
function createSoftwareIqAnalyticsConsentCookie(consent, hostname = typeof window === "undefined" ? "" : window.location.hostname, protocol = typeof window === "undefined" ? "" : window.location.protocol) {
  const sharedProductionDomain = hostname === "softwareiq.io" || hostname.endsWith(".softwareiq.io");
  return [
    `${SOFTWAREIQ_ANALYTICS_CONSENT_COOKIE}=${consent}`,
    "Path=/",
    "Max-Age=31536000",
    "SameSite=Lax",
    sharedProductionDomain ? "Domain=softwareiq.io" : "",
    sharedProductionDomain && protocol === "https:" ? "Secure" : ""
  ].filter(Boolean).join("; ");
}
function persistSoftwareIqAnalyticsConsent(consent) {
  if (typeof document === "undefined") return;
  document.cookie = createSoftwareIqAnalyticsConsentCookie(consent);
  window.dispatchEvent(new CustomEvent(SOFTWAREIQ_ANALYTICS_CONSENT_EVENT, { detail: { consent } }));
}
function openSoftwareIqAnalyticsPreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SOFTWAREIQ_ANALYTICS_PREFERENCES_EVENT));
}
var SOFTWAREIQ_ANALYTICS_CONSENT_BOOTSTRAP_SCRIPT = `(()=>{window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){window.dataLayer.push(arguments)};const n="${SOFTWAREIQ_ANALYTICS_CONSENT_COOKIE}",c=document.cookie.split(";").map(v=>v.trim()).find(v=>v.startsWith(n+"=")),v=c&&c.slice(n.length+1),accepted=v==="accepted";window.gtag("consent","default",{analytics_storage:accepted?"granted":"denied",ad_storage:"denied",ad_user_data:"denied",ad_personalization:"denied",wait_for_update:500});window.gtag("set",{ads_data_redaction:true,allow_google_signals:false,allow_ad_personalization_signals:false})})();`;
function readSoftwareIqThemePreference() {
  if (typeof document === "undefined") return null;
  const cookieTheme = readCookieValue(document.cookie, SOFTWAREIQ_THEME_COOKIE);
  if (isSoftwareIqTheme(cookieTheme)) return cookieTheme;
  try {
    const storedTheme = window.localStorage.getItem(SOFTWAREIQ_THEME_STORAGE_KEY);
    if (isSoftwareIqTheme(storedTheme)) {
      document.cookie = createSoftwareIqThemeCookie(storedTheme);
      return storedTheme;
    }
    return null;
  } catch {
    return null;
  }
}
function resolveSoftwareIqTheme() {
  const explicitTheme = readSoftwareIqThemePreference();
  if (explicitTheme) return explicitTheme;
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}
function createSoftwareIqThemeCookie(theme, hostname = typeof window === "undefined" ? "" : window.location.hostname, protocol = typeof window === "undefined" ? "" : window.location.protocol) {
  const sharedProductionDomain = hostname === "softwareiq.io" || hostname.endsWith(".softwareiq.io");
  return [
    `${SOFTWAREIQ_THEME_COOKIE}=${theme}`,
    "Path=/",
    "Max-Age=31536000",
    "SameSite=Lax",
    sharedProductionDomain ? "Domain=softwareiq.io" : "",
    sharedProductionDomain && protocol === "https:" ? "Secure" : ""
  ].filter(Boolean).join("; ");
}
function persistSoftwareIqTheme(theme) {
  if (typeof document === "undefined") return;
  document.cookie = createSoftwareIqThemeCookie(theme);
  try {
    window.localStorage.setItem(SOFTWAREIQ_THEME_STORAGE_KEY, theme);
  } catch {
  }
}
function applySoftwareIqTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}
var SOFTWAREIQ_THEME_BOOTSTRAP_SCRIPT = `(()=>{try{const n="${SOFTWAREIQ_THEME_COOKIE}",k="${SOFTWAREIQ_THEME_STORAGE_KEY}",c=document.cookie.split(";").map(v=>v.trim()).find(v=>v.startsWith(n+"=")),cv=c&&c.slice(n.length+1),valid=v=>v==="light"||v==="dark";let stored=null;try{stored=localStorage.getItem(k)}catch{}const explicit=valid(cv)?cv:valid(stored)?stored:null,t=explicit||(matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light");if(!valid(cv)&&valid(stored)){const h=location.hostname,p=[n+"="+stored,"Path=/","Max-Age=31536000","SameSite=Lax"];if(h==="softwareiq.io"||h.endsWith(".softwareiq.io")){p.push("Domain=softwareiq.io");if(location.protocol==="https:")p.push("Secure")}document.cookie=p.join("; ")}document.documentElement.dataset.theme=t;document.documentElement.style.colorScheme=t}catch{}})();`;
var navigationIconPaths = {
  "operating-kpis": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("path", { d: "M4 18V6" }),
    /* @__PURE__ */ jsx2("path", { d: "M4 18h16" }),
    /* @__PURE__ */ jsx2("path", { d: "m7 14 4-4 3 2 5-6" })
  ] }),
  "job-postings": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("path", { d: "M8 7V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V7" }),
    /* @__PURE__ */ jsx2("rect", { x: "3", y: "7", width: "18", height: "12", rx: "2" }),
    /* @__PURE__ */ jsx2("path", { d: "M3 12.5c5.5 2 12.5 2 18 0" }),
    /* @__PURE__ */ jsx2("path", { d: "M10 13h4" })
  ] }),
  "developer-signals": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("path", { d: "m8.5 7-5 5 5 5" }),
    /* @__PURE__ */ jsx2("path", { d: "m15.5 7 5 5-5 5" }),
    /* @__PURE__ */ jsx2("path", { d: "m14 4-4 16" })
  ] }),
  "market-benchmarks": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("path", { d: "M4 18V6" }),
    /* @__PURE__ */ jsx2("path", { d: "M4 18h16" }),
    /* @__PURE__ */ jsx2("path", { d: "m7 14 4-4 3 2 5-6" }),
    /* @__PURE__ */ jsx2("path", { d: "M7 8h3v3H7z" }),
    /* @__PURE__ */ jsx2("path", { d: "M14 13h5v5h-5z" })
  ] }),
  "fundamental-strategies": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("path", { d: "M4 18V6" }),
    /* @__PURE__ */ jsx2("path", { d: "M4 18h16" }),
    /* @__PURE__ */ jsx2("path", { d: "m7 14 4-4 3 2 5-6" }),
    /* @__PURE__ */ jsx2("circle", { cx: "19", cy: "6", r: "1" })
  ] }),
  "data-and-research-teams": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("ellipse", { cx: "12", cy: "5.5", rx: "8", ry: "3" }),
    /* @__PURE__ */ jsx2("path", { d: "M4 5.5v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" }),
    /* @__PURE__ */ jsx2("path", { d: "M4 11.5v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" })
  ] }),
  "venture-capital": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("path", { d: "M14 4c3-1 5-1 6-1 0 1 0 3-1 6l-6 6-4-4 5-7Z" }),
    /* @__PURE__ */ jsx2("path", { d: "m9 11-4 1-2 3 6 1" }),
    /* @__PURE__ */ jsx2("path", { d: "m13 15 1 6 3-2 1-5" }),
    /* @__PURE__ */ jsx2("circle", { cx: "16", cy: "7", r: "1.5" }),
    /* @__PURE__ */ jsx2("path", { d: "m7 18-3 3" })
  ] }),
  "strategy-and-competitive-intelligence": /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2("circle", { cx: "12", cy: "12", r: "9" }),
    /* @__PURE__ */ jsx2("circle", { cx: "12", cy: "12", r: "5" }),
    /* @__PURE__ */ jsx2("circle", { cx: "12", cy: "12", r: "1.5" }),
    /* @__PURE__ */ jsx2("path", { d: "m15.5 8.5 4-4" }),
    /* @__PURE__ */ jsx2("path", { d: "M16 4h4v4" })
  ] })
};
function SoftwareIqNavigationIcon({
  name,
  className = ""
}) {
  return /* @__PURE__ */ jsx2(
    "svg",
    {
      className: `siq-navigation-icon ${className}`.trim(),
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      focusable: "false",
      children: navigationIconPaths[name]
    }
  );
}
var brandTokens = {
  color: {
    primary: "#2952a3",
    primaryStrong: "#18336c",
    accent: "#0f8a6a",
    accentBright: "#70dfc0",
    footer: "#0a0c12",
    text: "#102235",
    muted: "#59657c"
  },
  fontFamily: '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", Arial, sans-serif',
  maxContentWidth: "1180px"
};
var SOFTWAREIQ_SOCIAL_LINKS = {
  x: "https://twitter.com/software__iq",
  linkedin: "https://www.linkedin.com/company/softwareiq"
};
var SOFTWAREIQ_DATASETS = [
  {
    id: "operating-kpis",
    icon: "operating-kpis",
    number: "01",
    label: "Operating KPIs",
    eyebrow: "Financial fundamentals",
    summary: "Source-backed operating KPIs and guidance, normalized across company disclosures.",
    analysisSummary: "Build comparable company panels, calibrate estimates, and trace normalized observations back to source evidence.",
    publicPath: "/products/operating-kpis",
    analysisPath: "/kpis/vendor/table",
    documentationPath: "/documentation/api/operating-kpis",
    sampleAnalysisPath: "/company/mdb/keymetrics",
    analysisPrefixes: ["/kpis"]
  },
  {
    id: "job-postings",
    icon: "job-postings",
    number: "02",
    label: "Job Postings",
    eyebrow: "Hiring demand",
    summary: "Daily employer hiring observations normalized across ATS providers, functions, sales roles, and investment themes.",
    analysisSummary: "Research hiring momentum, quota-bearing capacity, company-specific investment, and selected-market SI demand.",
    publicPath: "/products/job-postings",
    analysisPath: "/hiring/vendor/trend",
    documentationPath: "/documentation/api/job-postings",
    sampleAnalysisPath: "/company/mdb/jobpostings",
    analysisPrefixes: ["/hiring"]
  },
  {
    id: "developer-signals",
    icon: "developer-signals",
    number: "03",
    label: "Adoption Signals",
    eyebrow: "Open-source adoption",
    summary: "Normalized project and package activity for measuring product adoption across developer ecosystems.",
    analysisSummary: "Compare adoption momentum by project, company, and category with explicit ecosystem and coverage context.",
    publicPath: "/products/adoption-signals",
    analysisPath: "/adoption/project/trends",
    documentationPath: "/documentation/adoption-signals",
    sampleAnalysisPath: "/company/mdb/adoption",
    analysisPrefixes: ["/adoption", "/developer"]
  },
  {
    id: "market-benchmarks",
    icon: "market-benchmarks",
    number: "04",
    label: "Market Benchmarks",
    eyebrow: "Public-market context",
    summary: "Software index performance, historical valuation benchmarks, and a market map spanning companies and categories.",
    analysisSummary: "Compare sector returns and valuation trends, then explore market structure by company, category, ownership status, and value metric.",
    publicPath: "/products/market-benchmarks",
    analysisPath: "/benchmarks/index",
    documentationPath: null,
    sampleAnalysisPath: null,
    analysisPrefixes: ["/benchmarks"]
  }
];
var SOFTWAREIQ_DATA_DOCUMENTATION = [
  { id: "documentation-home", label: "Documentation home", path: "/documentation" },
  { id: "api-operating-kpis", label: "Operating KPIs reference", path: "/documentation/api/operating-kpis" },
  { id: "api-job-postings", label: "Job Postings reference", path: "/documentation/api/job-postings" },
  { id: "developer-signals", label: "Adoption Signals reference", path: "/documentation/adoption-signals" },
  { id: "bulk-delivery", label: "Bulk delivery & warehouse", path: "/documentation/bulk-delivery" },
  { id: "mcp", label: "MCP access and setup", path: "/documentation/mcp" }
];
var stripTrailingSlash = (value) => value.replace(/\/+$/, "");
function createSoftwareIqUrls({
  marketingBaseUrl = "https://www.softwareiq.io",
  researchBaseUrl = "https://research.softwareiq.io",
  documentationBaseUrl = marketingBaseUrl
} = {}) {
  const marketing = stripTrailingSlash(marketingBaseUrl);
  const research = stripTrailingSlash(researchBaseUrl);
  return {
    marketing,
    research,
    home: `${marketing}/`,
    about: `${marketing}/about`,
    pricing: `${marketing}/pricing`,
    documentation: `${marketing}/documentation`,
    contact: `${marketing}/contact`,
    privacy: `${marketing}/privacy`,
    terms: `${marketing}/termsandconditions`,
    marketCoverage: `${research}/marketinsights/coverage/coverage/table`,
    advancedMarketCoverage: `${research}/marketinsights/coverage`,
    industryMetrics: `${research}/kpis/vendor/table`,
    benchmarks: `${research}/benchmarks/index`,
    securityAnalysis: `${research}/company/mdb/keymetrics`
  };
}
function createSoftwareIqDatasetNavigation({
  marketingBaseUrl = "https://www.softwareiq.io",
  researchBaseUrl = "https://research.softwareiq.io",
  documentationBaseUrl = marketingBaseUrl
} = {}) {
  const marketing = stripTrailingSlash(marketingBaseUrl);
  const research = stripTrailingSlash(researchBaseUrl);
  const documentation = stripTrailingSlash(documentationBaseUrl);
  return {
    items: SOFTWAREIQ_DATASETS.map((item) => ({
      ...item,
      publicHref: item.publicPath ? `${marketing}${item.publicPath}` : null,
      analysisHref: `${research}${item.analysisPath}`,
      documentationHref: item.documentationPath ? `${documentation}${item.documentationPath}` : null,
      sampleAnalysisHref: item.sampleAnalysisPath ? `${research}${item.sampleAnalysisPath}` : null
    })),
    resources: [
      { id: "all-datasets", label: "All datasets", href: `${marketing}/platform` },
      ...SOFTWAREIQ_DATA_DOCUMENTATION.map((item) => ({
        ...item,
        href: `${documentation}${item.path}`
      }))
    ]
  };
}
function SoftwareIqDatasetMenuPanel({
  items,
  resources,
  selectedId,
  onSelect,
  datasetDestination = "overview",
  onNavigate,
  mobile = false,
  className = ""
}) {
  const selected = items.find((item) => item.id === selectedId) || items[0];
  if (!selected) return null;
  const selectedPublicHref = selected.publicHref;
  const selectedDocumentation = selected.documentationHref ? resources.find((item) => item.href === selected.documentationHref) : void 0;
  const handleNavigate = (event, href, item, kind) => {
    if (!onNavigate) return;
    event.preventDefault();
    onNavigate(href, item, kind);
  };
  return /* @__PURE__ */ jsxs2("div", { className: `siq-dataset-panel${mobile ? " siq-dataset-panel--mobile" : ""} ${className}`.trim(), children: [
    /* @__PURE__ */ jsxs2("div", { className: "siq-dataset-panel__rail", "aria-label": "Available datasets", children: [
      /* @__PURE__ */ jsx2("p", { className: "siq-dataset-panel__section-label", children: "Datasets" }),
      /* @__PURE__ */ jsx2("div", { children: items.map((item) => {
        const active = item.id === selected.id;
        const destinationKind = datasetDestination === "analysis" || !item.publicHref ? "analysis" : "overview";
        const destinationHref = destinationKind === "analysis" ? item.analysisHref : item.publicHref || item.analysisHref;
        return /* @__PURE__ */ jsxs2(
          "a",
          {
            className: `siq-dataset-panel__dataset${active ? " is-selected" : ""}`,
            href: destinationHref,
            onMouseEnter: () => onSelect?.(item.id),
            onFocus: () => onSelect?.(item.id),
            onClick: (event) => {
              onSelect?.(item.id);
              handleNavigate(event, destinationHref, item, destinationKind);
            },
            children: [
              /* @__PURE__ */ jsx2("span", { className: "siq-dataset-panel__number", children: /* @__PURE__ */ jsx2(SoftwareIqNavigationIcon, { name: item.icon }) }),
              /* @__PURE__ */ jsxs2("span", { children: [
                /* @__PURE__ */ jsx2("strong", { children: item.label }),
                /* @__PURE__ */ jsx2("small", { children: item.eyebrow })
              ] })
            ]
          },
          item.id
        );
      }) })
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "siq-dataset-panel__detail", "aria-live": "polite", children: [
      /* @__PURE__ */ jsxs2("div", { className: "siq-dataset-panel__heading", children: [
        /* @__PURE__ */ jsx2("span", { children: /* @__PURE__ */ jsx2(SoftwareIqNavigationIcon, { name: selected.icon }) }),
        /* @__PURE__ */ jsxs2("div", { children: [
          /* @__PURE__ */ jsx2("p", { children: selected.eyebrow }),
          /* @__PURE__ */ jsx2("h2", { children: selected.label })
        ] })
      ] }),
      /* @__PURE__ */ jsx2("p", { className: "siq-dataset-panel__summary", children: selected.summary }),
      /* @__PURE__ */ jsxs2("div", { className: "siq-dataset-panel__analysis", children: [
        /* @__PURE__ */ jsx2("strong", { children: "What you can analyze" }),
        /* @__PURE__ */ jsx2("p", { children: selected.analysisSummary })
      ] }),
      /* @__PURE__ */ jsxs2("div", { className: "siq-dataset-panel__actions", children: [
        selectedPublicHref && /* @__PURE__ */ jsxs2(
          "a",
          {
            href: selectedPublicHref,
            onClick: (event) => handleNavigate(event, selectedPublicHref, selected, "overview"),
            children: [
              "Dataset overview ",
              /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", children: "\u2197" })
            ]
          }
        ),
        /* @__PURE__ */ jsxs2(
          "a",
          {
            className: "siq-dataset-panel__primary-action",
            href: selected.analysisHref,
            onClick: (event) => handleNavigate(event, selected.analysisHref, selected, "analysis"),
            children: [
              "Open analysis ",
              /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", children: "\u2192" })
            ]
          }
        )
      ] }),
      selectedDocumentation && /* @__PURE__ */ jsxs2("nav", { className: "siq-dataset-panel__resources", "aria-label": "Dataset resources", children: [
        /* @__PURE__ */ jsx2("span", { children: "Resources" }),
        /* @__PURE__ */ jsx2("div", { children: /* @__PURE__ */ jsx2(
          "a",
          {
            href: selectedDocumentation.href,
            onClick: (event) => handleNavigate(event, selectedDocumentation.href, selectedDocumentation, "resource"),
            children: selectedDocumentation.label
          }
        ) })
      ] })
    ] })
  ] });
}
function SoftwareIqConsentBanner({
  open,
  onAccept,
  onReject,
  privacyHref = "https://www.softwareiq.io/privacy#cookies"
}) {
  if (!open) return null;
  return /* @__PURE__ */ jsxs2("section", { className: "siq-consent", role: "dialog", "aria-label": "Analytics cookie choices", "aria-live": "polite", children: [
    /* @__PURE__ */ jsxs2("div", { className: "siq-consent__copy", children: [
      /* @__PURE__ */ jsx2("strong", { children: "Your privacy choices" }),
      /* @__PURE__ */ jsxs2("p", { children: [
        "We use optional analytics cookies to understand how people use SoftwareIQ and improve the product. ",
        " ",
        /* @__PURE__ */ jsx2("a", { href: privacyHref, children: "Learn more" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "siq-consent__actions", children: [
      /* @__PURE__ */ jsx2("button", { type: "button", className: "siq-consent__reject", onClick: onReject, children: "Continue without analytics" }),
      /* @__PURE__ */ jsx2("button", { type: "button", className: "siq-consent__accept", onClick: onAccept, children: "Allow analytics" })
    ] })
  ] });
}
var XIcon = () => /* @__PURE__ */ jsx2("svg", { viewBox: "0 0 24 24", "aria-hidden": "true", children: /* @__PURE__ */ jsx2("path", { d: "M18.9 2.3h3.7l-8.1 9.3L24 21.7h-7.4l-5.8-7.6-6.6 7.6H.5l8.6-9.8L0 2.3h7.6l5.2 6.9 6.1-6.9Zm-1.3 17.2h2L6.5 4.4H4.3l13.3 15.1Z" }) });
var LinkedInIcon = () => /* @__PURE__ */ jsx2("svg", { viewBox: "0 0 24 24", "aria-hidden": "true", children: /* @__PURE__ */ jsx2("path", { d: "M5.1 3.5a1.9 1.9 0 1 1 0 3.8 1.9 1.9 0 0 1 0-3.8ZM3.5 8.9h3.2V20H3.5V8.9Zm5.2 0h3.1v1.5h.1c.4-.8 1.5-1.9 3.3-1.9 3.5 0 4.1 2.3 4.1 5.3V20h-3.2v-5.5c0-1.3 0-3-1.9-3s-2.2 1.4-2.2 2.9V20H8.7V8.9Z" }) });
function SoftwareIqLogo({ src, className }) {
  return /* @__PURE__ */ jsx2("img", { className, src, alt: "SoftwareIQ" });
}
function SoftwareIqFooter({
  documentationLink,
  companyOverview,
  marketingBaseUrl,
  researchBaseUrl,
  logoSrc,
  className = "",
  style
}) {
  const urls = createSoftwareIqUrls({ marketingBaseUrl, researchBaseUrl });
  const datasetNavigation = createSoftwareIqDatasetNavigation({ marketingBaseUrl, researchBaseUrl });
  return /* @__PURE__ */ jsx2("footer", { className: `siq-footer ${className}`.trim(), style, children: /* @__PURE__ */ jsxs2("div", { className: "siq-footer__inner", children: [
    /* @__PURE__ */ jsxs2("div", { className: "siq-footer__about", children: [
      /* @__PURE__ */ jsx2("a", { className: "siq-footer__logo", href: urls.home, "aria-label": "SoftwareIQ home", children: /* @__PURE__ */ jsx2(SoftwareIqLogo, { src: logoSrc }) }),
      /* @__PURE__ */ jsx2("p", { children: "Financial analytics for software companies" }),
      /* @__PURE__ */ jsxs2("div", { className: "siq-footer__socials", children: [
        /* @__PURE__ */ jsx2("a", { href: SOFTWAREIQ_SOCIAL_LINKS.x, target: "_blank", rel: "noreferrer", "aria-label": "SoftwareIQ on X", children: /* @__PURE__ */ jsx2(XIcon, {}) }),
        /* @__PURE__ */ jsx2("a", { href: SOFTWAREIQ_SOCIAL_LINKS.linkedin, target: "_blank", rel: "noreferrer", "aria-label": "SoftwareIQ on LinkedIn", children: /* @__PURE__ */ jsx2(LinkedInIcon, {}) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "siq-footer__links", children: [
      /* @__PURE__ */ jsxs2("nav", { "aria-label": "Company links", children: [
        /* @__PURE__ */ jsx2("h2", { children: "Company" }),
        /* @__PURE__ */ jsx2("a", { href: urls.home, children: "Homepage" }),
        /* @__PURE__ */ jsx2("a", { href: companyOverview?.href || urls.about, children: companyOverview?.label || "About Us" }),
        /* @__PURE__ */ jsx2("a", { href: urls.pricing, children: "Pricing" }),
        documentationLink || /* @__PURE__ */ jsx2("a", { href: urls.documentation, children: "Documentation" }),
        /* @__PURE__ */ jsx2("a", { href: urls.contact, children: "Contact Us" }),
        /* @__PURE__ */ jsx2("a", { href: urls.privacy, children: "Privacy Policy" }),
        /* @__PURE__ */ jsx2("button", { className: "siq-footer__preference", type: "button", "data-siq-cookie-preferences": true, children: "Cookie Choices" }),
        /* @__PURE__ */ jsx2("a", { href: urls.terms, children: "Terms and Conditions" })
      ] }),
      /* @__PURE__ */ jsxs2("nav", { "aria-label": "Dataset links", children: [
        /* @__PURE__ */ jsx2("h2", { children: "Datasets" }),
        datasetNavigation.items.map((item) => /* @__PURE__ */ jsx2("a", { href: item.publicHref || item.analysisHref, children: item.label }, item.id))
      ] })
    ] })
  ] }) });
}
export {
  SOFTWAREIQ_ANALYTICS_CONSENT_BOOTSTRAP_SCRIPT,
  SOFTWAREIQ_ANALYTICS_CONSENT_COOKIE,
  SOFTWAREIQ_ANALYTICS_CONSENT_EVENT,
  SOFTWAREIQ_ANALYTICS_PREFERENCES_EVENT,
  SOFTWAREIQ_DATASETS,
  SOFTWAREIQ_DATA_DOCUMENTATION,
  SOFTWAREIQ_DOCUMENTATION_PALETTES,
  SOFTWAREIQ_SOCIAL_LINKS,
  SOFTWAREIQ_THEME_BOOTSTRAP_SCRIPT,
  SOFTWAREIQ_THEME_COOKIE,
  SOFTWAREIQ_THEME_STORAGE_KEY,
  SoftwareIqConsentBanner,
  SoftwareIqDatasetMenuPanel,
  SoftwareIqFooter,
  SoftwareIqLogo,
  SoftwareIqMcpGuide,
  SoftwareIqNavigationIcon,
  applySoftwareIqTheme,
  brandTokens,
  createSoftwareIqAnalyticsConsentCookie,
  createSoftwareIqDatasetNavigation,
  createSoftwareIqThemeCookie,
  createSoftwareIqUrls,
  openSoftwareIqAnalyticsPreferences,
  persistSoftwareIqAnalyticsConsent,
  persistSoftwareIqTheme,
  readSoftwareIqAnalyticsConsent,
  readSoftwareIqThemePreference,
  resolveSoftwareIqTheme
};
